<template>
	<div class="app-input-wrapper">
		<label @click="focusOnInputEl">
			<div v-if="label" class="app-input-label">
				{{ label }}
				<span v-if="requiredField" class="app-input-required">*</span>
			</div>
			<input
				ref="inputEl"
				v-model.trim="model"
				class="app-input-element"
				:class="{
					'input-valid': !error?.length,
					'input-invalid': error?.length,
					'input-disabled': disabled,
					'input-with-icon': iconName,
				}"
				:type="type"
				:name="name"
				:disabled="disabled"
				:placeholder="placeholder"
				@blur="handleBlur"
				@change="emits('change', $event)"
				@input="emits('input', $event)"
				@focus="handleFocus($event)"
			/>

			<Icon v-if="iconName" :name="'local:' + iconName" class="app-input-icon" />
		</label>

		<div class="app-input-error">
			<span v-if="error">{{ error }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, useTemplateRef, onMounted } from 'vue';
	import Inputmask from 'inputmask';

	interface IProps {
		label?: string;
		placeholder?: string;
		name?: string;
		error?: string | undefined;
		disabled?: boolean;
		type?: string;
		mask?: string;
		focusOnMount?: boolean;
		requiredField?: boolean;
		iconName?: string;
	}

	const props = withDefaults(defineProps<IProps>(), {
		label: '',
		placeholder: '',
		name: '',
		error: undefined,
		disabled: false,
		type: 'text',
		mask: '',
		iconName: '',
	});

	const model = defineModel<string | number>({ required: true, default: '' });
	const emits = defineEmits(['blur', 'change', 'input', 'focus']);

	const isFocused = ref(false);
	const inputEl = useTemplateRef('inputEl');

	onMounted(() => {
		if (props.focusOnMount) {
			focusOnInputEl();
		}

		if (inputEl.value) {
			Inputmask(props.mask).mask(inputEl.value);
		}
	});

	function handleFocus($event: FocusEvent) {
		emits('focus', $event);
		isFocused.value = true;
	}

	function handleBlur($event: FocusEvent) {
		emits('blur', $event);
		isFocused.value = false;
	}

	function focusOnInputEl() {
		inputEl.value?.focus();
	}
</script>

<style scoped lang="scss">
	.input-el {
		width: 100%;
		height: 100%;
	}

	.icon-reset {
		right: 16px;
		top: 50%;
		width: 20px;
		cursor: pointer;
		position: absolute;
		transform: translateY(-50%);
		-ms-transform: translateY(-50%);
		--svg-icon-width: 20px;
		--svg-icon-height: 20px;
		--svg-icon-color: var(--color-grey2);
	}
</style>
